// src/components/AppShell/TopBar.tsx
import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGuild } from '../../lib/GuildContext';
import { useAuth } from '../../lib/AuthContext';
import { apiFetch } from '../../lib/api';
import { formatGuildName } from '../../lib/formatGuildName';
import { formatRelative } from '../../lib/formatTime';
import './AppShell.css';

export function TopBar() {
  const { guilds, selectedGuildId, setSelectedGuildId } = useGuild();
  const { user } = useAuth();
  // Deliberately NOT useBotHealth() — that now hits the creator-gated
  // /api/creator/bot-health, which would 403 for every regular user
  // on every single page load (TopBar renders everywhere). This tiny,
  // non-sensitive endpoint stays available to all logged-in users.
  const { data: syncStatus } = useQuery({
    queryKey: ['market-sync-status'],
    queryFn: () => apiFetch<{ lastSyncedAt: string | null }>('/api/market-sync-status'),
    refetchInterval: 30_000,
  });
  const selectedGuild = guilds.find((g) => g.guild_id === selectedGuildId);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the menu on any click outside it — standard dropdown behavior,
  // otherwise it stays open until the user clicks the avatar again.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="topbar">
      <div className="topbar__left">
        {guilds.length > 0 && (
          <label className="guild-selector">
            <span className="guild-selector__icon" aria-hidden="true">
              {selectedGuild ? formatGuildName(selectedGuild.guild_name, selectedGuild.guild_id).slice(0, 2).toUpperCase() : '??'}
            </span>
            <select
              value={selectedGuildId ?? ''}
              onChange={(e) => setSelectedGuildId(e.target.value)}
              aria-label="Select server"
            >
              {guilds.map((g) => (
                <option key={g.guild_id} value={g.guild_id}>
                  {formatGuildName(g.guild_name, g.guild_id)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="topbar__right">
        {/* Bot status/sync freshness — placeholder values until the
            Bot Health page (and a real status endpoint) exist. */}
        <span className="status-pill">
          <span className="status-dot" aria-hidden="true" />
          Bot online
        </span>
        <span className="sync-status">
          Market synced {syncStatus?.lastSyncedAt ? formatRelative(syncStatus.lastSyncedAt) : '—'}
        </span>

        {user && (
          <div className="user-menu" ref={menuRef}>
            <button
              className="avatar"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              aria-label="Account menu"
            >
              {user.username.slice(0, 2).toUpperCase()}
            </button>
            {menuOpen && (
              <div className="user-menu__dropdown" role="menu">
                <p className="user-menu__name">{user.username}</p>
                <a className="user-menu__item" href="/auth/logout" role="menuitem">
                  Log out
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
